import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Stack,
  Box,
  Paper,
  IconButton,
} from '@mui/material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import SnackbarInfo from '../common/snackbar-info.component';
import { updateDataById } from '../../services/videos.service';
import Iconify from '../common/iconify.component';
import LanguageCodes from '../form-elements/language-codes.component';
import { LANGUAGE_CODES } from '../../config/const.config';
import Upload from '../common/upload.component';

const VideoSubtitles = ({
  videoDetail,
  type,
  handleRefreshData,
  formValueChangedCallback,
}) => {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      subtitles:
        videoDetail.subtitles?.length > 0
          ? videoDetail.subtitles
          : [
              {
                languageCode: '',
                languageLabel: '',
                subtitleUrl: '',
              },
            ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'subtitles',
  });
  const watchSubtitles = watch('subtitles');

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const hasDuplicateLanguageSubtitle = watchSubtitles.some(
      (item, index) =>
        watchSubtitles.findIndex(
          (v) => v.languageCode === item.languageCode
        ) !== index
    );
    if (hasDuplicateLanguageSubtitle) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message:
            'Multiple subtitles found for same language. Please remove it.',
        });
      }, 50);
      setFormSubmitted(false);
      return;
    }

    const payload = {
      ...data,
      type,
    };
    updateDataById(videoDetail.id, payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Video subtitles updated successfully.',
        });
        formValueChangedCallback(false);
        handleRefreshData();
        setFormSubmitted(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  useEffect(() => {
    if (Object.keys(dirtyFields)?.length > 0) {
      formValueChangedCallback(true);
    } else {
      formValueChangedCallback(false);
    }
  }, [Object.keys(dirtyFields).length]);

  return (
    <Box component={Paper} padding={2}>
      <form id="video-subtitles-form" onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={2}>
          {fields.map((field1, index) => (
            <React.Fragment key={field1.id}>
              <Grid item xs={2.5}>
                <Controller
                  name={`subtitles.${index}.languageCode`}
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <LanguageCodes
                      id="language_code"
                      name="language_code"
                      label="Language Code *"
                      defaultValue={value}
                      onChange={(e) => {
                        onChange(e);
                        setValue(
                          `subtitles.${index}.languageLabel`,
                          LANGUAGE_CODES[e.target.value]?.label
                        );
                      }}
                      sx={{ width: '100%' }}
                      error={
                        errors?.subtitles?.[index]?.languageCode?.message || ''
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2.5}>
                <Controller
                  name={`subtitles.${index}.languageLabel`}
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(60),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Label *"
                      type="text"
                      fullWidth
                      variant="filled"
                      error={!!errors?.subtitles?.[index]?.languageLabel}
                      helperText={
                        errors?.subtitles?.[index]?.languageLabel?.message ||
                        null
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={fields.length > 1 ? 4 : 3.5}>
                <Controller
                  name={`subtitles.${index}.subtitleUrl`}
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...validationRules.OPTIONAL_URL,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="URL *"
                      type="text"
                      fullWidth
                      variant="filled"
                      error={!!errors?.subtitles?.[index]?.subtitleUrl}
                      helperText={
                        errors?.subtitles?.[index]?.subtitleUrl?.message || null
                      }
                    />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={fields.length > 1 ? 2.5 : 3.5}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Box>
                  <Upload
                    title="Upload SRT File"
                    updateImage={(v) =>
                      setValue(`subtitles.${index}.subtitleUrl`, v)
                    }
                    s3Folder="subtitles"
                    aspectRatio="16.9"
                    allowedSize={[[1920, 1080]]}
                    setSnackbarInfo={setSnackbarInfo}
                    type="subtitle"
                    details={videoDetail}
                    sourceType="subtitleFileName"
                    isMultipleSubtitle
                    subtitleLabel={getValues(
                      `subtitles.${index}.languageLabel`
                    )}
                  />
                </Box>
              </Grid>

              {fields.length > 1 && (
                <Grid
                  item
                  xs={0.2}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <IconButton
                    aria-label="close"
                    onClick={() => remove(index)}
                    size="small"
                  >
                    <Iconify icon="material-symbols:delete-outline" />
                  </IconButton>
                </Grid>
              )}
            </React.Fragment>
          ))}

          <Grid item xs={12} sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                append({
                  languageCode: '',
                  languageLabel: '',
                  subtitleUrl: '',
                })
              }
            >
              Add Subtitle
            </Button>
          </Grid>
        </Grid>
      </form>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2.5 }}
      >
        <Button
          type="submit"
          form="video-subtitles-form"
          color="primary"
          variant="contained"
          disabled={formSubmitted || fields.length === 0}
        >
          Save Subtitles
        </Button>
        <Typography variant="body2" color="secondary">
          * indicates a required field
        </Typography>
      </Stack>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

VideoSubtitles.propTypes = {
  videoDetail: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  handleRefreshData: PropTypes.func.isRequired,
  formValueChangedCallback: PropTypes.func.isRequired,
};

export default VideoSubtitles;
